/**
 * Editor de **puertas combinadas** (`combinada_medidas`, Sección 2).
 *
 * Tabla dinámica de pares `[ancho, alto]` (cm): "+ Medida" añade una fila y `×`
 * por fila la quita (réplica del SSR). Presentacional: el estado lo posee
 * `PreciosForm`. Usa `components/ui/Table`.
 */
import {
  Button,
  Input,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
} from "@/components/ui";

import type { CombinadaRow } from "../precios.transform";

interface CombinadaMedidasEditorProps {
  rows: CombinadaRow[];
  onChange: (rows: CombinadaRow[]) => void;
}

export function CombinadaMedidasEditor({
  rows,
  onChange,
}: CombinadaMedidasEditorProps) {
  function setCell(index: number, field: keyof CombinadaRow, value: string) {
    onChange(rows.map((row, i) => (i === index ? { ...row, [field]: value } : row)));
  }

  function addRow() {
    onChange([...rows, { ancho: "", alto: "" }]);
  }

  function removeRow(index: number) {
    onChange(rows.filter((_, i) => i !== index));
  }

  return (
    <div className="flex flex-col gap-2">
      <span className="text-sm font-medium text-slate-700">
        Puertas combinadas (ancho × alto, cm)
      </span>
      <Table>
        <TableHead>
          <TableRow>
            <TableHeaderCell>Ancho</TableHeaderCell>
            <TableHeaderCell>Alto</TableHeaderCell>
            <TableHeaderCell className="w-12">
              <span className="sr-only">Acciones</span>
            </TableHeaderCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.length === 0 ? (
            <TableRow>
              <TableCell colSpan={3} className="text-center text-slate-500">
                Sin medidas combinadas.
              </TableCell>
            </TableRow>
          ) : (
            rows.map((row, index) => (
              <TableRow key={index}>
                <TableCell>
                  <Input
                    type="number"
                    step="0.1"
                    inputMode="decimal"
                    aria-label={`Ancho de la medida combinada ${index + 1}`}
                    value={row.ancho}
                    onChange={(event) => setCell(index, "ancho", event.target.value)}
                  />
                </TableCell>
                <TableCell>
                  <Input
                    type="number"
                    step="0.1"
                    inputMode="decimal"
                    aria-label={`Alto de la medida combinada ${index + 1}`}
                    value={row.alto}
                    onChange={(event) => setCell(index, "alto", event.target.value)}
                  />
                </TableCell>
                <TableCell>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    aria-label={`Quitar medida combinada ${index + 1}`}
                    onClick={() => removeRow(index)}
                  >
                    ×
                  </Button>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
      <div>
        <Button type="button" variant="secondary" size="sm" onClick={addRow}>
          + Medida
        </Button>
      </div>
    </div>
  );
}
